import React, { useState } from "react";
import {
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DangerousIcon from "@mui/icons-material/Dangerous";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { NetsOperation, NetsProcessResponseFlag, NetsProcessResponseFragment } from "../generated/gql/graphql";
import { gql } from "@apollo/client";
import { isSuperUser } from "../util/active_user_context";

export const NETS_PROCESS_RESPONSE_FRAGMENT = gql`
    fragment NetsProcessResponse on NetsProcessResponse {
        codeMapping {
            responseSource
            responseCode
            technicalReason
            flags
            retryPolicy
        }
        responseText
        error
    }
`;

export function NetsResponseCode({
    operation,
    response,
}: {
    operation: NetsOperation;
    response?: NetsProcessResponseFragment | null;
}) {
    if (!response || !response.codeMapping) {
        if (response?.error) {
            return <Typography>Error: {response.error}</Typography>;
        }
        return <Typography>–</Typography>;
    }

    const {
        codeMapping: { responseSource, responseCode },
    } = response;
    const isSuccess = responseCode === "OK";
    let displayText = responseCode;

    if (responseSource && !isSuccess) {
        displayText += " / " + responseSource;
    }

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Button
                variant={isSuccess ? "outlined" : "contained"}
                onClick={!isSuccess ? handleClick : undefined}
                size="large"
                color={isSuccess ? "success" : "error"}
                sx={{
                    textTransform: "none",
                    padding: "2px 8px",
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                }}
                startIcon={isSuccess ? <CheckCircleIcon color="success" /> : <DangerousIcon />}
            >
                {displayText}
            </Button>
            <Popover
                open={open && !isSuccess}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <NetsResponseCodeCard operation={operation} response={response} />
            </Popover>
        </>
    );
}

const FLAG_LABELS: Record<NetsProcessResponseFlag, string> = {
    [NetsProcessResponseFlag.MerchantError]: "Feil hos oss (merchant)",
    [NetsProcessResponseFlag.ShouldConsumerContactBank]: "Kunden bør kontakte banken",
    [NetsProcessResponseFlag.SoftDeclined]: "Krever 3DSecure godkjenning",
};

const ALL_FLAGS: NetsProcessResponseFlag[] = [
    NetsProcessResponseFlag.MerchantError,
    NetsProcessResponseFlag.ShouldConsumerContactBank,
    NetsProcessResponseFlag.SoftDeclined,
];

const OPERATION_LABELS: Record<NetsOperation, string> = {
    [NetsOperation.Sale]: "Korttrekk",
    [NetsOperation.Capture]: "Korttrekk",
    [NetsOperation.Verify]: "Kortregistrering",
    [NetsOperation.Credit]: "Kortrefusjon",
    [NetsOperation.Auth]: "Autorisering",
};

function NetsResponseCodeCard({
    operation,
    response,
}: {
    operation: NetsOperation;
    response: NetsProcessResponseFragment;
}) {
    const { codeMapping, responseText } = response;

    if (!codeMapping) {
        return null;
    }

    const { responseSource, responseCode, technicalReason, flags, retryPolicy } = codeMapping;

    return (
        <Paper sx={{ p: 2, maxWidth: response.error ? 800 : 400 }}>
            <Typography variant="h6" gutterBottom>
                Nets transaksjon avslått
            </Typography>

            <Table size="small">
                <TableBody>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: "bold" }}>
                            Type
                        </TableCell>
                        <TableCell>{OPERATION_LABELS[operation]}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: "bold" }}>
                            Feilkilde
                        </TableCell>
                        <TableCell>{responseSource}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: "bold" }}>
                            Feilkode
                        </TableCell>
                        <TableCell>{responseCode}</TableCell>
                    </TableRow>
                    {responseText && (
                        <TableRow>
                            <TableCell component="th" sx={{ fontWeight: "bold" }}>
                                Tekst
                            </TableCell>
                            <TableCell>{responseText}</TableCell>
                        </TableRow>
                    )}
                    {technicalReason && (
                        <TableRow>
                            <TableCell colSpan={2} sx={{ marginBottom: "10px" }}>
                                <div style={{ marginTop: "10px", marginBottom: "20px" }}>
                                    <p style={{ fontWeight: "bold", marginBottom: "4px" }}>Forklaring:</p>
                                    {technicalReason}
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell colSpan={2}>
                            <List dense disablePadding>
                                {ALL_FLAGS.map((flag) => (
                                    <ListItem key={flag} disablePadding sx={{ py: 0.25 }}>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            {flags.includes(flag) ? (
                                                <CheckBoxIcon fontSize="small" color="primary" />
                                            ) : (
                                                <CheckBoxOutlineBlankIcon fontSize="small" color="disabled" />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={FLAG_LABELS[flag]}
                                            slotProps={{ primary: { variant: "body2" } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </TableCell>
                    </TableRow>
                    {retryPolicy && (
                        <TableRow>
                            <TableCell component="th" sx={{ fontWeight: "bold" }}>
                                Retry Policy
                            </TableCell>
                            <TableCell>{retryPolicy}</TableCell>
                        </TableRow>
                    )}

                    {isSuperUser() && response.error ? (
                        <TableRow>
                            <TableCell component="th" sx={{ fontWeight: "bold" }}>
                                Error
                            </TableCell>
                            <TableCell>
                                <div style={{ maxWidth: "600px", maxHeight: "300px", overflow: "auto" }}>
                                    <pre>{response.error}</pre>
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
        </Paper>
    );
}
